import { Fragment, type ReactNode } from 'react';

/**
 * Renderizador de Markdown mínimo.
 *
 * Produce elementos de React, nunca HTML crudo. Es deliberado: los contenidos y
 * los textos legales los escribe la institución desde la consola, y con
 * `dangerouslySetInnerHTML` un `<script>` pegado en un artículo se ejecutaría en
 * el navegador de todos sus pacientes. Acá lo peor que puede pasar es que un
 * asterisco no se convierta en negrita.
 *
 * Soporta lo que efectivamente se usa: títulos, párrafos, negrita, cursiva,
 * código, listas, tablas y enlaces.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  return <div className={className}>{renderBlocks(source)}</div>;
}

function renderBlocks(source: string): ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  let index = 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index]!;

    if (line.trim() === '') {
      index++;
      continue;
    }

    // Títulos
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1]!.length;
      const text = heading[2]!;
      const classes: Record<number, string> = {
        1: 'text-2xl font-semibold mt-6 mb-3 tracking-tight',
        2: 'text-lg font-semibold mt-6 mb-2 tracking-tight',
        3: 'text-base font-semibold mt-5 mb-1.5',
        4: 'text-[15px] font-semibold mt-4 mb-1',
      };
      const Tag = (`h${Math.min(level + 1, 6)}`) as 'h2' | 'h3' | 'h4' | 'h5';
      blocks.push(
        <Tag key={key++} className={classes[level] ?? classes[3]}>
          {renderInline(text)}
        </Tag>,
      );
      index++;
      continue;
    }

    // Tablas
    if (line.includes('|') && lines[index + 1]?.match(/^\s*\|?[\s:|-]+\|/)) {
      const header = splitRow(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index]!.includes('|')) {
        rows.push(splitRow(lines[index]!));
        index++;
      }
      blocks.push(
        <div key={key++} className="overflow-x-auto my-4 -mx-1 px-1">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                {header.map((cell, cellIndex) => (
                  <th key={cellIndex} className="text-left font-semibold py-2 pr-4 align-top">
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border last:border-0">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="py-2 pr-4 align-top text-fg-muted">
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Listas
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      const items: string[] = [];

      while (index < lines.length) {
        const current = lines[index]!;
        const match = ordered
          ? current.match(/^\s*\d+\.\s+(.*)$/)
          : current.match(/^\s*[-*]\s+(.*)$/);
        if (!match) break;
        items.push(match[1]!);
        index++;
      }

      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag
          key={key++}
          className={`my-3 space-y-1.5 pl-5 ${ordered ? 'list-decimal' : 'list-disc'} marker:text-fg-subtle`}
        >
          {items.map((item, itemIndex) => (
            <li key={itemIndex} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ListTag>,
      );
      continue;
    }

    // Párrafo: se juntan las líneas seguidas hasta el próximo corte.
    const paragraph: string[] = [];
    while (
      index < lines.length &&
      lines[index]!.trim() !== '' &&
      !lines[index]!.match(/^(#{1,4}\s|\s*[-*]\s|\s*\d+\.\s)/) &&
      !lines[index]!.includes('|')
    ) {
      paragraph.push(lines[index]!);
      index++;
    }

    if (paragraph.length > 0) {
      blocks.push(
        <p key={key++} className="my-3 leading-relaxed">
          {renderInline(paragraph.join(' '))}
        </p>,
      );
    } else {
      index++;
    }
  }

  return blocks;
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

/** Negrita, cursiva, código y enlaces. */
function renderInline(text: string): ReactNode {
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(pattern).filter((part) => part !== '');

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={index}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={index} className="bg-surface px-1.5 py-0.5 rounded text-[0.9em] font-mono">
              {part.slice(1, -1)}
            </code>
          );
        }

        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) {
          const href = link[2]!;
          // Solo http(s) y mailto. Sin este filtro, un `javascript:` escrito en
          // el CMS se ejecutaría al tocar el enlace.
          const safe = /^(https?:|mailto:)/i.test(href);
          if (!safe) return <Fragment key={index}>{link[1]}</Fragment>;
          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              {link[1]}
            </a>
          );
        }

        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
}
